import React, { useEffect, useCallback, useMemo } from 'react';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import ConversationsList from './components/ConversationsList';
import MessageHeader from './components/MessageHeader';
import EmptyState from './components/EmptyState';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useMessageContext } from '../../hook/useMessageContext';
import { useDispatch, useSelector } from 'react-redux';
import { roomsPaginationSelector, roomsSelector } from '../../selectors/selectors';
import { getMessagesForSelectedRoom } from '../../redux/messages/actions';
import { getJWtDetails } from '../../utils/AppUtils';
import { ROUTES } from '../../types/global/routes.types';

const Messages: React.FC = () => {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const rooms = useSelector(roomsSelector);
    const pagination = useSelector(roomsPaginationSelector);
    const dispatch = useDispatch();
    const { user_id } = getJWtDetails();

    const { activeRoomId, setActiveRoomId, setIsLoading, limit, page } = useMessageContext();

    const userScopedRooms = useMemo(() => {
        const currentUserId = String(user_id ?? '');
        if (!currentUserId) return [];
        const getParticipantId = (participant: any) => {
            if (!participant) return '';
            if (typeof participant === 'string') return String(participant);
            return String(participant?._id ?? participant?.id ?? '');
        };
        return (rooms ?? []).filter(room => {
            const senderId = getParticipantId(room?.sender);
            const receiverIds = Array.isArray(room?.receiver) ? room.receiver.map(getParticipantId) : [];
            return senderId === currentUserId || receiverIds.includes(currentUserId);
        });
    }, [rooms, user_id]);

    // Keep active room in sync with the URL: `/messages` alone = empty state; `/messages/:roomId` = that room.
    useEffect(() => {
        if (roomId) {
            setActiveRoomId(roomId);
        } else {
            setActiveRoomId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    // Prevent viewing conversations that do not belong to the logged-in user.
    useEffect(() => {
        if (!activeRoomId) return;
        const canAccessRoom = userScopedRooms.some(room => room._id === activeRoomId);
        if (!canAccessRoom) {
            setActiveRoomId(null);
            navigate(ROUTES.MESSAGES, { replace: true });
        }
    }, [activeRoomId, userScopedRooms, navigate, setActiveRoomId]);

    const handleGetRoomMessages = useCallback(() => {
        setIsLoading(true);
        if (activeRoomId) {
            dispatch(
                getMessagesForSelectedRoom({
                    roomID: activeRoomId,
                    userID: user_id,
                    pagination: {
                        limit: limit,
                        page: page,
                    },
                }),
            );
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRoomId]);

    useEffect(() => {
        handleGetRoomMessages();
    }, [handleGetRoomMessages]);

    useEffect(() => {
        window.addEventListener('popstate', event => {
            if (event.state) {
                setActiveRoomId(null);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MetaTags title="Your Conversations & Messages" description="">
            <Container
                // disableGutters
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: { xs: 0, sm: -65, xl: -100 },
                    right: 0,
                    bottom: 0,
                    marginLeft: { xs: '0px', sm: '65px', mdlg: '80px', xl: '100px' },
                    marginRight: { xs: 0, sm: -300 },
                    // paddingLeft: '0px',
                    // paddingRight: { xs: '0px', sm: '40px' },
                    // paddingTop: { mdLg: '10px' },
                    overflowX: 'auto',
                    minHeight: '100dvh',
                    //  height: '100dvh',
                    width: '100dvw',
                    minWidth: '100dvw',
                    zIndex: 9999,
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '64px',
                        zIndex: 10,
                    }}>
                    <MessageHeader hideBottomBorder={!activeRoomId} />
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        top: '64px',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        gap: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Conversations list - hidden on mobile when a conversation is active */}
                    {(!isMobile || !activeRoomId) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: { xs: '100%', md: 384 },
                                bgcolor: theme[themeStyle].deep_grey,
                                borderRight: `1px solid ${alpha(theme[themeStyle].grey, 0.12)}`,
                                overflow: 'hidden',
                                zIndex: 5,
                            }}>
                            <ConversationsList
                                rooms={userScopedRooms}
                                activeRoomId={activeRoomId}
                                onRoomClicked={roomId => setActiveRoomId(roomId)}
                                pagination={pagination}
                            />
                        </Box>
                    )}

                    {/* Message content area */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: { xs: 0, md: 384 },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            zIndex: 1,
                            bgcolor: theme[themeStyle].secondaryBg,
                        }}>
                        {activeRoomId ? (
                            <Outlet context={{ roomId: activeRoomId }} />
                        ) : (
                            <EmptyState
                                message="Select a Conversation"
                                subMessage="Select a chat from the list to start messaging with the industry's top talent."
                            />
                        )}
                    </Box>
                </Box>
            </Container>
        </MetaTags>
    );
};

export { Messages };
